import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import { WazeIcon, GoogleMapsIcon } from './BrandIcons';

import apiClient from '../services/api-client';
import { DEFAULT_LOCATION, isLocationUnset } from '../utils/location';

const DEFAULT_LAT = DEFAULT_LOCATION.lat;
const DEFAULT_LNG = DEFAULT_LOCATION.lng;

export default function LocationModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [copied, setCopied] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');

  const handleCopyAddress = async () => {
    if (!fullAddress) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullAddress);
      } else {
        const ta = document.createElement('textarea');
        ta.value = fullAddress;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setSnackbarMsg('Dirección copiada');
      setSnackbarOpen(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // ignore
    }
  };

  const handleShareLink = async (url, label) => {
    const shareData = {
      title: friendlyName || 'Ubicación',
      text: `Cómo llegar a ${friendlyName || 'la ubicación'}${fullAddress ? ` — ${fullAddress}` : ''}`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
        } else {
          const ta = document.createElement('textarea');
          ta.value = url;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        setSnackbarMsg(`Enlace de ${label} copiado`);
        setSnackbarOpen(true);
      }
    } catch (e) {
      // user cancelled or error — ignore
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLocation(null);

    apiClient.get('org/location', {}, { action: 'fetch_org_location' })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || res.data || {};
        setLocation({
          lat: typeof data.lat === 'number' ? data.lat : DEFAULT_LAT,
          lng: typeof data.lng === 'number' ? data.lng : DEFAULT_LNG,
          full_address: data.full_address || '',
          friendly_name: data.friendly_name || '',
          static_map: data.static_map || '',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open]);

  const lat = location?.lat ?? DEFAULT_LAT;
  const lng = location?.lng ?? DEFAULT_LNG;
  const fullAddress = location?.full_address || '';
  const friendlyName = location?.friendly_name || '';
  const staticMap = location?.static_map || '';

  const notConfigured = isLocationUnset({ location: { lat, lng }, full_address: fullAddress });

  const wazeUrl = `https://www.waze.com/ul?ll=${lat},${lng}&navigate=yes`;
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ m: 2.5 }}>
            No se pudo cargar la ubicación.
          </Alert>
        )}

        {!loading && !error && notConfigured && (
          <Alert severity="info" sx={{ m: 2.5 }}>
            Ubicación no configurada
          </Alert>
        )}

        {!loading && !error && !notConfigured && (
          <>
            {/* Header row: icon + org name (left) + close (right) */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 2.5, pb: 1 }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center">
                <PlaceOutlinedIcon sx={{ color: 'secondary.main', fontSize: 22 }} />
                <Typography sx={{ fontSize: 20, fontWeight: 900 }}>
                  {friendlyName || 'Ubicación'}
                </Typography>
              </Stack>
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                  color: (t) => t.palette.grey[500],
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>

            {/* Address row: address text + copy icon, top-aligned */}
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="flex-start"
              sx={{ px: 2.5, pb: 2.5 }}
            >
              <Typography sx={{ fontSize: 14.7, fontWeight: 700, color: 'text.secondary' }}>
                {fullAddress || 'Dirección no configurada'}
              </Typography>
              {fullAddress && (
                <IconButton
                  size="small"
                  onClick={handleCopyAddress}
                  aria-label="copiar dirección"
                  sx={{
                    color: 'secondary.main',
                    p: 0.5,
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  {copied
                    ? <CheckOutlinedIcon sx={{ fontSize: 18 }} />
                    : <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              )}
            </Stack>

            {/* Share buttons on solid background */}
            <Stack direction="row" spacing={1.5} sx={{ p: 2.5, pt: 0 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleShareLink(wazeUrl, 'Waze')}
                startIcon={<WazeIcon />}
                endIcon={<ShareOutlinedIcon sx={{ fontSize: 16, opacity: 0.6 }} />}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 800,
                  py: 1.2,
                  borderColor: '#4871E6',
                  borderWidth: 2,
                  color: '#4871E6',
                }}
              >
                Waze
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => handleShareLink(mapsUrl, 'Maps')}
                startIcon={<GoogleMapsIcon />}
                endIcon={<ShareOutlinedIcon sx={{ fontSize: 16, opacity: 0.6 }} />}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 800,
                  py: 1.2,
                  borderColor: '#238048',
                  borderWidth: 2,
                  color: '#238048',
                }}
              >
                Maps
              </Button>
            </Stack>
          </>
        )}
      </DialogContent>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snackbarMsg}
      />
    </Dialog>
  );
}
